// Predictive maintenance (preview). Base = /api/maintenance/predictive-maintenance.
//
// pm-service scores every live charging connector once a day: P(a NEW incident starts within
// 7/30/90/180/365 days), per tier. The tiers answer different questions:
//
//   device   hardware-side incidents (faults, charging-start failures, silence)
//   start    charging-start failures only — connected but never charged, 0-unit sessions
//   offline  heartbeat silence episodes
//   site     supply over/under-voltage — a grid condition at the site, not a charger defect
//
// The numbers rank connectors for attention. They are not alarms and nothing acts on them.
import axiosInstance from "../axiosInstance";

const BASE = "/api/maintenance/predictive-maintenance";

export type Tier = "device" | "start" | "offline" | "site";
export const TIERS: Tier[] = ["device", "start", "offline", "site"];

export interface TierSummary {
  tier: Tier;
  connectors: number;
  high: number;
  elevated: number;
  in_failure_now: number;
  mean_p30: number | null;
  model_version: string | null;
  scored_at: string | null;
}

export interface PredictiveSummary {
  enabled: boolean;
  preview: boolean;
  as_of: string | null;
  model_version: string | null;
  tiers: TierSummary[];
}

export interface ConnectorRiskRow {
  device_connector_id: number;
  device_id: number;
  as_of: string;
  connector_no: number | null;
  device_name: string | null;
  display_name: string | null;
  vendor: string | null;
  model: string | null;
  firmware: string | null;
  station_id: number | null;
  station_name: string | null;
  station_guid: string | null;
  org_name: string | null;
  device_p7: number | null;
  device_p30: number | null;
  device_p90: number | null;
  device_rank_pct: number | null;
  site_p30: number | null;
  start_p30: number | null;
  offline_p30: number | null;
  sort_p30: number | null;
  in_failure_now: boolean;
  last_incident_at: string | null;
  last_incident_kind: string | null;
  model_version: string | null;
}

export interface CurvePoint {
  h: number;
  p: number;
}

export interface TierRisk {
  tier: Tier;
  as_of: string;
  p7: number | null;
  p30: number | null;
  p90: number | null;
  p180: number | null;
  p365: number | null;
  rank_pct: number | null;
  in_failure_now: boolean;
  last_incident_at: string | null;
  last_incident_kind: string | null;
  model_version: string | null;
  curve: CurvePoint[];
}

export interface HistoryPoint {
  as_of: string;
  tier: Tier;
  p30: number | null;
}

export interface IncidentEvent {
  kind: string;
  severity: string | null;
  started_at: string;
  ended_at: string | null;
  error_code: string | null;
  detail: Record<string, unknown>;
}

export interface ConnectorHead {
  device_connector_id: number;
  device_id: number;
  connector_no: number | null;
  capacity_kwh: number | null;
  device_name: string | null;
  display_name: string | null;
  vendor: string | null;
  model: string | null;
  firmware: string | null;
  station_id: number | null;
  station_name: string | null;
  org_name: string | null;
  station_guid: string | null;
  first_event_at: string | null;
  last_event_at: string | null;
}

export interface ConnectorRiskDetail {
  connector: ConnectorHead;
  as_of: string | null;
  tiers: TierRisk[];
  in_failure_now: boolean;
  last_incident_at: string | null;
  last_incident_kind: string | null;
  history: HistoryPoint[];
  events: IncidentEvent[];
  model_version: string | null;
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ListParams {
  tier: Tier;
  q?: string;
  min_p30?: number;
  in_failure_now?: boolean;
  page?: number;
  page_size?: number;
}

export const predictionsApi = {
  summary: () => axiosInstance.get<PredictiveSummary>(`${BASE}/summary`),
  list: (params: ListParams) => axiosInstance.get<Paged<ConnectorRiskRow>>(`${BASE}/connectors`, { params }),
  detail: (connectorId: number) => axiosInstance.get<ConnectorRiskDetail>(`${BASE}/connectors/${connectorId}`),
};
